import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * HeroConstellation
 *
 * Animated sequential flow visual for the main landing-page hero.
 * Five Sampler hiring-flow steps reveal one at a time (fade + slide from
 * below), each holding for ~2s. Once all five are visible, the sequence
 * pauses then fades out and restarts.
 */

const STEPS = [
    {
        label: 'STEP 01',
        title: 'Assessment completed',
        subtitle: 'Candidate answers 6 role-specific questions',
    },
    {
        label: 'STEP 02',
        title: 'Talent Card generated',
        subtitle: 'AI scores across 6 performance dimensions',
    },
    {
        label: 'STEP 03',
        title: 'Candidates compared',
        subtitle: 'Employer views ranked Talent Cards',
    },
    {
        label: 'STEP 04',
        title: 'Trial booked',
        subtitle: 'One day. Real work. Real performance.',
    },
    {
        label: 'STEP 05',
        title: 'Trial complete. Candidate hired.',
        subtitle: 'Hire with confidence. No surprises.',
    },
];

// Timings (seconds)
const STEP_INTERVAL = 2;      // delay between each card appearing
const HOLD_AFTER_ALL = 2;     // pause once all five are visible
const FADE_OUT_DURATION = 0.8;
const CYCLE_MS = (STEPS.length * STEP_INTERVAL + HOLD_AFTER_ALL + FADE_OUT_DURATION) * 1000;

const HeroConstellation = () => {
    const [cycle, setCycle] = useState(0);

    useEffect(() => {
        const id = setInterval(() => setCycle((c) => c + 1), CYCLE_MS);
        return () => clearInterval(id);
    }, []);

    return (
        <div
            className="relative w-full h-full flex items-center justify-center p-4"
            data-testid="hero-constellation"
        >
            <AnimatePresence mode="wait">
                <motion.div
                    key={cycle}
                    className="flex flex-col gap-3 w-full text-center"
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: { duration: FADE_OUT_DURATION } }}
                >
                    {STEPS.map((step, i) => (
                        <motion.div
                            key={`${cycle}-${i}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * STEP_INTERVAL, duration: 0.5, ease: 'easeOut' }}
                            data-testid={`hero-step-${i + 1}`}
                        >
                            <div
                                className="text-xs tracking-wider leading-none"
                                style={{ color: '#8B5CF6', fontFamily: 'Unbounded, sans-serif' }}
                            >
                                {step.label}
                            </div>
                            <div
                                className="text-xl font-bold leading-tight mt-2"
                                style={{ color: '#0A0A0A', fontFamily: 'Unbounded, sans-serif' }}
                            >
                                {step.title}
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export default HeroConstellation;
